import { createContext, useContext, useEffect, useState } from "react";
import { adminApi } from "../utils/admin-api-axios";

const AdminAuthContext = createContext();

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error("useAdminAuth must be used within an AdminAuthProvider");
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [adminUser, setAdminUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Check if admin is already logged in on mount
  useEffect(() => {
    const token = localStorage.getItem("admintoken");
    if (token) {
      verifyAdminToken(token);
    } else {
      setLoading(false);
    }
  }, []);

  const clearAuthData = () => {
    localStorage.removeItem("admintoken");
    setAdminUser(null);
  };

  const verifyAdminToken = async (token) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || "http://localhost:4000/api"}/admin/users`, {
        method: "GET",
        headers: {
          admintoken: token,
          "Content-Type": "application/json",
        },
      });

      console.log("Token verification response:", response);

      // Check if response is ok (status 200-299)
      if (response.ok) {
        // Parse the response body to check for actual success
        const responseData = await response.json();
        console.log("Response data:", responseData);

        // Check if the response indicates success
        // Adjust this condition based on your backend response structure
        if (responseData.success !== false && !responseData.error) {
          // Token is valid, set admin user
          setAdminUser({ 
            id: responseData.admin?.id || 'admin', 
            role: responseData.admin?.role || 'admin',
            ...responseData.admin 
          });
        } else {
          // Backend returned 200 but with error/failure in body
          console.log("Token verification failed - backend error:", responseData.message || responseData.error);
          clearAuthData();
        }
      } else {
        // HTTP error status
        console.log("Token verification failed - HTTP status:", response.status);
        
        // Try to get error details from response body
        try {
          const errorData = await response.json();
          console.log("Error details:", errorData);
        } catch (parseError) {
          console.log("Could not parse error response");
        }
        
        clearAuthData();
      }
    } catch (error) {
      console.error("Token verification failed - network/other error:", error);
      clearAuthData();
    } finally {
      setLoading(false);
    }
  };

  const loginAdmin = async (username, password) => {
    setLoading(true);
    setError("");

    try {
      const data = await adminApi.loginAdmin(username, password);

      if (data.success) {
        localStorage.setItem("admintoken", data.token);
        setAdminUser(data.admin);
        setError("");
        return { success: true };
      } else {
        setError(data.message || "Login failed");
        return { success: false, error: data.message };
      }
    } catch (error) {
      const errorMessage = error.message || "Network error. Please try again.";
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const logoutAdmin = () => {
    clearAuthData();
    setError('');
  };

  // Add a method to manually refresh token verification
  const refreshAuth = async () => {
    const token = localStorage.getItem("admintoken");
    if (token) {
      setLoading(true);
      await verifyAdminToken(token);
    }
  };

  const value = {
    adminUser,
    loading,
    error,
    loginAdmin,
    logoutAdmin,
    refreshAuth,
    isAuthenticated: !!adminUser,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};